import { motion } from "motion/react"
import { cn } from "src/common/utils"
import { isRootlyBranded } from "src/utils/brandTheme"

import { LoadingSpinner } from "./loading-spinner"

interface LogoSpinnerProps {
  className?: string
  text?: string
  size?: "sm" | "md" | "lg"
  brandTheme?: string | null
}

const tierzeroMarkPaths = (
  <>
    <path d="M9.084 0H0V12.78H2.1656V5.4624C2.1656 4.2688 1.8168 3.0992 1.1592 2.0864H9.084V0Z" />
    <path d="M12.3392 8.1784V15.496C12.3392 16.6896 12.688 17.8592 13.3456 18.872H5.4208V20.9584H14.5048V8.1784H12.3392Z" />
    <path d="M12.0336 0L0 20.9584H2.4712L14.5048 0H12.0336Z" />
  </>
)

const rootlyMarkPaths = (
  <>
    <path d="M7.18305 14.7294C7.18305 14.175 6.9878 13.7233 6.57675 13.3537C5.11754 12.0292 1.43866 12.0806 0.143867 12.2141L0 10.8178C0.2158 10.7972 5.29223 10.3044 7.51188 12.3065C8.22094 12.943 8.58061 13.7541 8.58061 14.7191" />
    <path d="M7.41931 14.7294C7.41931 13.7643 7.77898 12.9533 8.48803 12.3167C10.7077 10.3147 15.7944 10.8075 15.9999 10.8281L15.8561 12.2141C14.5613 12.0806 10.8824 12.0293 9.42317 13.3537C9.01212 13.7233 8.81687 14.175 8.81687 14.7294" />
    <path d="M13.5029 3.89799C13.5029 3.89799 11.1907 3.24092 10.3378 4.50373C9.47461 5.746 11.8176 7.49134 13.5029 3.89799Z" />
    <path d="M15.0238 7.37841C15.0238 7.37841 12.8761 6.31067 11.7971 7.38868C10.7181 8.46669 12.7014 10.6124 15.0238 7.37841Z" />
    <path d="M7.85098 0.941177C7.85098 0.941177 6.36094 2.81999 7.19331 4.09306C8.02568 5.36613 10.5331 3.86719 7.85098 0.941177V0.941177Z" />
    <path d="M7.88184 6.19775C7.88184 6.19775 6.34041 8.0355 7.13167 9.3291C7.92294 10.6227 10.4714 9.2059 7.88184 6.19775Z" />
    <path d="M2.35327 3.89799C2.35327 3.89799 4.66541 3.24092 5.51833 4.50373C6.37126 5.76653 4.03856 7.49134 2.35327 3.89799Z" />
    <path d="M0.832397 7.37841C0.832397 7.37841 2.98012 6.31067 4.05912 7.38868C5.13812 8.46669 3.14454 10.6124 0.832397 7.37841Z" />
  </>
)

const tierzeroSizeClasses = {
  sm: "h-7 w-5",
  md: "h-10 w-7",
  lg: "h-[70px] w-[50px]",
}

const rootlySizeClasses = {
  sm: "h-7 w-7",
  md: "h-10 w-10",
  lg: "h-[60px] w-[60px]",
}

const genericSizeClasses = {
  sm: "h-5 w-5",
  md: "h-8 w-8",
  lg: "h-12 w-12",
}

export const LogoSpinner = ({
  className,
  text,
  size = "md",
  brandTheme,
}: LogoSpinnerProps) => {
  if (brandTheme == null) {
    return (
      <div className={cn("flex flex-col items-center gap-3", className)}>
        <LoadingSpinner className={genericSizeClasses[size]} />
        {text && (
          <motion.span
            className="text-sm text-muted-foreground"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            {text}
          </motion.span>
        )}
      </div>
    )
  }

  // ROOTLY_HYBRID uses Rootly UI styling per brand_theme spec, even though the backend
  // does not consider it "Rootly-branded".
  const isRootly = isRootlyBranded(brandTheme) || brandTheme === "ROOTLY_HYBRID"
  const markPaths = isRootly ? rootlyMarkPaths : tierzeroMarkPaths
  const viewBox = isRootly ? "0 0 16 16" : "0 0 15 21"
  const sizeClasses = isRootly ? rootlySizeClasses : tierzeroSizeClasses
  const ghostColor1 = isRootly ? "#9370DB" : "#2E9AFF"
  const ghostColor2 = isRootly ? "#6B3FA0" : "#8b00ff"

  return (
    <div className={cn("flex flex-col items-center gap-3", className)}>
      <div className={cn("relative", sizeClasses[size])}>
        {/* Base mark */}
        <motion.svg
          viewBox={viewBox}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="absolute inset-0 z-20 h-full w-full"
          animate={{ scale: [1, 1.06, 1] }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <g fill="currentColor">{markPaths}</g>
        </motion.svg>

        {/* Primary ghost — brand color, fast drift */}
        <motion.svg
          viewBox={viewBox}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="absolute inset-0 h-full w-full blur-[0.5px]"
          animate={{
            x: [-2, 4, -3, 1.5, -2],
            y: [-2, 4, -3, 1.5, -2],
            opacity: [0.4, 0.9, 0.4, 0.8, 0.4],
          }}
          transition={{
            duration: 0.5,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "linear",
            times: [0, 0.2, 0.5, 0.8, 1],
          }}
        >
          <g fill={ghostColor1}>{markPaths}</g>
        </motion.svg>

        {/* Secondary ghost — slower, opposite drift */}
        <motion.svg
          viewBox={viewBox}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="absolute inset-0 h-full w-full"
          animate={{
            x: [0, 1.5, -2, 2, -1.5, 0],
            y: [0, -1.5, 2, -1, 0],
            opacity: [0.4, 0.8, 0.3, 0.9, 0.4],
          }}
          transition={{
            duration: 0.8,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "linear",
            times: [0, 0.3, 0.6, 0.8, 1],
          }}
        >
          <g fill={ghostColor2} fillOpacity={0.5}>
            {markPaths}
          </g>
        </motion.svg>

        {/* Periodic skew glitch */}
        <motion.svg
          viewBox={viewBox}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="absolute inset-0 z-10 h-full w-full"
          animate={{
            skewX: [0, -30, 0],
            scaleX: [1, 1.6, 1],
            opacity: [0, 0.4, 0],
          }}
          transition={{
            duration: 0.08,
            repeat: Infinity,
            repeatDelay: 2.5,
            ease: "linear",
            times: [0, 0.3, 1],
          }}
        >
          <g fill="currentColor">{markPaths}</g>
        </motion.svg>
      </div>

      {text && (
        <motion.span
          className="text-sm text-muted-foreground"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          {text}
        </motion.span>
      )}
    </div>
  )
}
